import { useTranslator } from "@eo-locale/react";

import Cookies from "js-cookie";
import { isEmpty } from "lodash";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { FaCheckCircle } from "react-icons/fa";

import React from "react";

import { useAppSelector } from "@/redux/store/hooks";
import { setHasCookieConsent } from "@/redux/features/cookie-consent/slice";
import {
  Box,
  Button,
  ButtonGroup,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Heading,
  HTMLWrapper,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  VisuallyHidden,
  Switch,
  Divider,
  Icon,
  Stack
} from "@/storybook-components";
import { removeGACookies } from "@/helpers/remove-ga-cookies";

interface CookieConsentProps {
  zIndex?: number;
}

const EnhancedCookieConsent = (props: CookieConsentProps) => {
  const [showDrawer, setShowDrawer] = useState(true);
  const { translate } = useTranslator();
  const closeDrawer = () => setShowDrawer(false);
  const [isAnalyticsCookieEnabled, setAnalyticsCookie] = useState(false);
  const { settings } = useAppSelector((state) => state.settings);
  const { disableGoogleAnalytics, cookiePolicy } = useAppSelector(
    (state) => state.features.features
  );
  const dispatch = useDispatch();

  const [showManageAllContent, setShowManageAllContent] = useState(false);
  let isCookiePolicyEnabled =
    cookiePolicy?.enabled && isEmpty(Cookies.get("cookie_consent"));
  const isBttAnalyticsEnabled = !disableGoogleAnalytics?.enabled;
  const isUserTrackingAnalyticsEnabled =
    settings?.trafficTrackingEnabled && !!settings?.googleAnalyticsAccount;
  const analyticsStatus =
    isUserTrackingAnalyticsEnabled || isBttAnalyticsEnabled;
  const [showCookieConsent, setShowCookieConsent] = useState(
    isCookiePolicyEnabled
  );
  const focusRef = React.useRef(null);

  const cookiesList = [
    { name: "essential", status: true },
    { name: "analytics", status: isAnalyticsCookieEnabled }
  ];

  const setCookieConsent = () => {
    // for setting the cookie consent cookie and closing the modal
    const currentDate = new Date();

    // Creating a expiry date for the cookie which is exactly after two years
    currentDate.setMonth(currentDate.getMonth() + 24);

    const cookieObject = {
      functional: true,
      analytics: isAnalyticsCookieEnabled
    };

    Cookies.set("cookie_consent", JSON.stringify(cookieObject), {
      expires: currentDate,
      path: "/",
      secure: true,
      sameSite: "strict"
    });
    const setGoogleAnalyticsStorage =
      (isUserTrackingAnalyticsEnabled || isBttAnalyticsEnabled) &&
      isAnalyticsCookieEnabled;

    /* global gtag */
    if (setGoogleAnalyticsStorage) {
      // gtag -> is from the third party google analytics
      // @ts-ignore
      gtag("consent", "update", {
        analytics_storage: "granted"
      });
    }
    setShowDrawer(false);

    setShowManageAllContent(false);
    setShowCookieConsent(false);
    dispatch(setHasCookieConsent(true));
  };

  const handleChange = (cookieName, value) => {
    if (cookieName === "analytics") {
      setAnalyticsCookie(value);
    }
  };

  const renderCookieReview = () => {
    return (
      <Text>{translate("common.cookieConsent.analyticsCookieReview")}</Text>
    );
  };

  const isCookieDisabled = (cookieName: string): boolean => {
    if (cookieName === "analytics") return analyticsStatus;
    return true;
  };

  const handleSwitchModalAction = () => {
    setShowDrawer(!showDrawer);
    setShowManageAllContent(!showManageAllContent);
  };

  const acceptAllCookies = () => {
    // for setting the cookie consent cookie and closing the modal
    const currentDate = new Date();

    // Creating a expiry date for the cookie which is exactly after two years
    currentDate.setMonth(currentDate.getMonth() + 24);

    const cookieObject = {
      functional: true,
      analytics: true
    };

    Cookies.set("cookie_consent", JSON.stringify(cookieObject), {
      expires: currentDate,
      path: "/",
      secure: true,
      sameSite: "strict"
    });

    if (isUserTrackingAnalyticsEnabled || isBttAnalyticsEnabled) {
      // tagmanager script will be present only when either of the ehq analytics or client
      // is switched on, else no point in granting the storage to consent.
      // no need to check for analytics storage because accept all -> analytics storage is granted

      // gtag -> is from the third party google analytics
      // @ts-ignore
      gtag("consent", "update", {
        analytics_storage: "granted"
      });
    }

    setShowDrawer(false);

    setShowManageAllContent(false);
    setShowCookieConsent(false);
    dispatch(setHasCookieConsent(true));
  };

  useEffect(() => {
    const cookieConsent = Cookies.get("cookie_consent");
    let isAnalyticsAccessGiven = false;

    try {
      isAnalyticsAccessGiven = cookieConsent
        ? JSON.parse(cookieConsent)?.analytics
        : false;
    } catch (error) {
      console.error("Error parsing cookieConsent:", error);
    }

    if (!isAnalyticsAccessGiven) {
      removeGACookies();
    }
  }, []);

  const renderToggle = (cookie) => {
    if (cookie.name === "essential") {
      return (
        <>
          <Icon
            as={FaCheckCircle}
            w={8}
            h={8}
            color="gray.300"
            aria-hidden="true"
          />
          <VisuallyHidden>
            {translate(`common.cookieConsent.essentialCookies`)}
          </VisuallyHidden>
        </>
      );
    }

    const handleKeyPress = (event) => {
      if (event.key === "Enter") {
        handleChange(cookie.name, !cookie.status);
      }
    };

    return (
      <>
        <VisuallyHidden>
          {translate(`common.cookieConsent.${cookie.name}Cookies`)}
        </VisuallyHidden>
        <Switch
          aria-label={translate(`common.cookieConsent.${cookie.name}Cookies`)}
          onKeyDown={(event) => handleKeyPress(event)}
          isDisabled={!isCookieDisabled(cookie.name)}
          isChecked={cookie.status}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            handleChange(cookie.name, e.target.checked)
          }
        />
      </>
    );
  };

  if (!showCookieConsent) return null;

  const renderCookies = () => {
    return cookiesList.map((cookie, index) => {
      const cookieListClass = isCookieDisabled(cookie.name)
        ? "gray.800"
        : "gray.500";
      return (
        <Box
          key={index}
          as="div"
          color={cookieListClass}
          pt="4"
          title={`${cookie.status ? "" : translate("common.cookieConsent.disabledTooltip")}`}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Box width="80%">
              <Text fontWeight="bold">
                {translate(`common.cookieConsent.${cookie.name}Cookies`)}
              </Text>
              <Text>
                {translate(
                  `common.cookieConsent.${cookie.name}CookiesDescription`
                )}
              </Text>
            </Box>

            <Box>{renderToggle(cookie)}</Box>
          </Box>
          <Divider my={2} />
        </Box>
      );
    });
  };

  const getManageCookieModal = () => {
    return (
      <Modal
        isOpen={showManageAllContent}
        closeOnOverlayClick={false}
        blockScrollOnMount
        size="xl"
        onClose={handleSwitchModalAction}
        initialFocusRef={focusRef}
      >
        <ModalOverlay />
        <ModalContent color="black" backgroundColor="white">
          <ModalHeader>
            <Heading as="h2" size="md">
              {translate("common.cookieConsent.manageCookies")}
            </Heading>
          </ModalHeader>
          <ModalBody ref={focusRef}>
            <HTMLWrapper
              html={translate("common.cookieConsent.aboutCookies")}
            />
            {renderCookies()}
            {!analyticsStatus && renderCookieReview()}
          </ModalBody>
          <ModalFooter justifyContent="flex-start">
            <ButtonGroup>
              <Button onClick={setCookieConsent}>
                {translate("common.saveAndClose")}
              </Button>
              <Button
                variant="secondary"
                key="cancel"
                onClick={handleSwitchModalAction}
              >
                {translate("common.cancel")}
              </Button>
            </ButtonGroup>
          </ModalFooter>
        </ModalContent>
      </Modal>
    );
  };

  return (
    <>
      {getManageCookieModal()}
      <Drawer
        placement="bottom"
        isOpen={showDrawer}
        closeOnOverlayClick={false}
        closeOnEsc={false}
        size="lg"
        onClose={closeDrawer}
      >
        <DrawerOverlay />
        <DrawerContent
          zIndex={2000}
          position="fixed"
          bottom={0}
          px={4}
          py={6}
          w="100%"
          bg="white"
          boxShadow="xs"
        >
          <DrawerHeader p={0}>
            {translate("common.cookieConsent.title")}
          </DrawerHeader>
          <DrawerBody p={0}>
            <HTMLWrapper
              html={translate("common.cookieConsent.newDescription")}
            />
            <Box pt={4}>
              <Stack direction={["column", "column", "row"]} spacing={2}>
                <Button onClick={setCookieConsent}>
                  {translate("common.cookieConsent.acceptEssential")}
                </Button>
                <Button onClick={acceptAllCookies}>
                  {translate("common.cookieConsent.acceptAll")}
                </Button>
                <Button variant="secondary" onClick={handleSwitchModalAction}>
                  {translate("common.cookieConsent.manageCookies")}
                </Button>
              </Stack>
            </Box>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
};

export default EnhancedCookieConsent;
